import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";

import {
  Alert,
  Button,
  Icon,
  Spinner,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  getErrorMessage,
  useGetAllRelationships,
  useGetCashWithdrawable,
  useGetInstantAchEligibility,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import { createAchTransfer } from "~/v2/api/rest/banking";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FAQ } from "../../../../../components/common/FAQ";
import { Layout } from "../../common/Layout";
import { getIraContributionTransferFields } from "../../common/util";
import { ACH_FAQ } from "../../link/ach";
import { FromTo, type FromToItem } from "../../transferForm/FromTo";
import {
  getCorrectAmount,
  type TransferSchema,
  zTransferSchema,
} from "../../transferForm/schema";
import { TransferFormButtons } from "../../transferForm/TransferFormButtons";
import { TransferFormFields } from "../../transferForm/TransferFormFields";

const ACHDepositPage = () => {
  const history = useHistory();

  const [isSubmitChecked, setIsSubmitChecked] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const { ach } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  const withdrawable = useGetCashWithdrawable({
    accountID: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  const instantAchEligibility = useGetInstantAchEligibility({
    accountID: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  // Find the first active (non-canceled, non-rejected) relationship for actual use
  const achRelationship = ach.relationships.find(
    (rel) => rel.status !== "CANCELED" && rel.status !== "REJECTED",
  );
  const hasACHRelationship = !!achRelationship;

  const form = useForm<TransferSchema>({
    resolver: zodResolver(zTransferSchema),
    mode: "onBlur",
  });

  // zod and react-hook-form do not play nice
  const amountString = form.watch("amount");
  const amount = getCorrectAmount(amountString);
  const isConfirmDisabled = amount <= 0;

  const alpacaAccountInfo: FromToItem = {
    imgSrc: "resources/images/logo-collapsed.svg",
    name: "Alpaca Account",
    account: currentAccount?.account_number ?? "",
  };

  const onBackClicked = () => {
    history.push(Path.ROUTE_FUNDING_DEPOSIT);
  };

  const deposit = useMutationRequest(
    (amount: number) => {
      setIsSubmitEnabled(false);
      return createAchTransfer(currentAccount?.id || "", {
        transfer_type: "ach",
        direction: "INCOMING",
        relationship_id: achRelationship?.id || "",
        amount: String(amount),
        ...getIraContributionTransferFields(currentAccount),
      });
    },
    {
      onSuccess: () => {
        // track in Amplitude
        AmplitudeProvider.dispatch(
          "deposit_submitted_v2",
          JSON.stringify({
            amount: amount ?? "-",
          }),
        );

        toastSuccess("Transfer created");

        withdrawable.refetch();
        instantAchEligibility.refetch();
        history.push(Path.ROUTE_FUNDING_HISTORY);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(
          `An error occurred trying to deposit funds: ${errorMessage}`,
        );
        setIsSubmitEnabled(true);
      },
    },
  );

  const onSubmitValid: SubmitHandler<TransferSchema> = ({ amount }) => {
    deposit.mutate(getCorrectAmount(amount));
  };

  const onSubmitInvalid = () => {
    toastError("Please enter a valid amount");
  };

  const BackButton = () => (
    <Button variant="secondary" onClick={onBackClicked} className="mb-8">
      <Icon name="ArrowLeftCircle" className="mr-2" />
      Back
    </Button>
  );

  const isLoading =
    !currentAccount ||
    !details ||
    ach.isLoading ||
    instantAchEligibility.isLoading;

  return (
    <Layout
      title="Deposit Funds via ACH"
      subtitle="Deposit funds into your account using your ACH connection."
      className="p-2"
      faq={<FAQ faq={ACH_FAQ} />}
    >
      {isLoading ? (
        <Spinner className="mx-auto" />
      ) : !hasACHRelationship ? (
        <div className="flex flex-col gap-4">
          <Text>No ACH connection Found.</Text>
          <BackButton />
        </div>
      ) : (
        <>
          <BackButton />

          <Text className="pb-4 font-semibold text-xl">Deposit Funds:</Text>

          <form
            onSubmit={form.handleSubmit(onSubmitValid, onSubmitInvalid)}
            className="w-full max-w-lg rounded border border-gray-200 p-4"
          >
            {instantAchEligibility.data?.eligible && (
              <Alert
                className="mb-4"
                variant="success"
                message={`Your account qualifies for Instant Funding up to $${Number(
                  instantAchEligibility.data.current_tier.transfer_limit,
                ).toLocaleString()}, one settled transaction at a time. Accounts linked via micro-deposits are not eligible.`}
              />
            )}

            <Alert
              variant="info"
              className="mb-4"
              message="Note: You will not be able to withdraw newly deposited money for 6 business days. Alpaca partners with BMO Harris Bank to process ACH transfers."
            />
            <FromTo
              from={{
                name: achRelationship.brand_name,
                account: achRelationship.account_name ?? "",
                imgSrc: achRelationship.logo,
              }}
              to={alpacaAccountInfo}
              className="mb-8"
            />
            <TransferFormFields form={form} isInputDisabled={isSubmitChecked} />
            <TransferFormButtons
              direction="deposit"
              isSubmitChecked={isSubmitChecked}
              setIsSubmitChecked={setIsSubmitChecked}
              isSubmitEnabled={isSubmitEnabled}
              isConfirmDisabled={isConfirmDisabled}
              amount={amountString}
            />
          </form>
        </>
      )}
    </Layout>
  );
};

export default ACHDepositPage;
